import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { ControlConfig, SelectOption } from '../../types/form.types';

/**
 * Custom dropdown select component with accessibility features.
 *
 * @remarks Provides a fully accessible custom select dropdown with keyboard
 * navigation, screen reader support, and reactive form integration. Implements
 * ARIA patterns for combobox/listbox interactions and supports type-ahead
 * functionality for improved user experience.
 */
@Component({
  selector: 'app-form-select',
  templateUrl: './form-select.component.html',
  styleUrls: ['./form-select.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SvgIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormSelectComponent implements OnInit, OnChanges {
  /** @remarks Configuration object defining the select behavior and options */
  @Input() controlConfig: ControlConfig;

  /** @remarks Form group that contains this select control */
  @Input() formGroup: FormGroup;

  /** @remarks External error state flag for custom validation scenarios */
  @Input() hasError: boolean = false;

  /** @remarks Reference to the select button element for focus management */
  @ViewChild('selectButton') selectButton!: ElementRef;

  /** @remarks Reference to the dropdown listbox element for scrolling */
  @ViewChild('listbox') listbox!: ElementRef;

  /** @remarks Current state of the dropdown (open/closed) */
  isDropdownOpen = false;

  /** @remarks Index of the currently focused option in the dropdown */
  currentOptionIndex = -1;

  /** @remarks Last character typed for type-ahead functionality */
  lastTypedChar = '';

  /** @remarks Index for cycling through matching options during type-ahead */
  lastMatchingIndex = 0;

  /**
   * Determines if the select is in an invalid state.
   *
   * @remarks Checks if the form control is invalid and has been interacted with
   * (touched or dirty) or if an external error flag is set. Used for styling
   * and error message display.
   *
   * @returns True if the select should show validation errors
   */
  get isInvalid(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return (control?.invalid && (control?.touched || control?.dirty)) || this.hasError;
  }

  /**
   * Determines if the select is in a valid state.
   *
   * @remarks Checks if the form control is valid and has been interacted with,
   * and no external error is present. Used for success styling indicators.
   *
   * @returns True if the select should show validation success
   */
  get isValid(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return control?.valid && (control?.touched || control?.dirty) && !this.hasError;
  }

  /**
   * Gets the placeholder text for the select.
   *
   * @remarks Returns the configured placeholder text or empty string if none set.
   *
   * @returns The placeholder text to display
   */
  get placeholder(): string {
    return this.controlConfig.placeholder || '';
  }

  /**
   * Gets the display label of the currently selected option.
   *
   * @remarks Finds the selected option by value and returns its display label.
   * Returns empty string if no option is selected or value doesn't match any option.
   *
   * @returns The label of the selected option or empty string
   */
  get selectedOption(): string {
    const control = this.formGroup?.get(this.controlConfig.name);
    const value = control?.value;
    if (value) {
      const option = this.controlConfig.options?.find(opt => opt.value === value);
      return option ? option.label : '';
    }
    return '';
  }

  /**
   * Gets the appropriate error message to display.
   *
   * @remarks Determines the correct error message based on validation failures,
   * particularly checking for required field violations when no option is selected.
   *
   * @returns The error message string or null if no errors
   */
  get errorMessage(): string | null {
    if (this.isInvalid) {
      // check if the select is required and if no value is selected
      if (this.controlConfig.required && !this.formGroup?.get(this.controlConfig.name)?.value) {
        return this.controlConfig.errorMessageRequired || '';
      }
      return this.controlConfig.errorMessage || '';
    }
    return null;
  }

  /**
   * Creates an instance of FormSelectComponent.
   *
   * @param elementRef - Reference to the component's DOM element for click outside detection
   */
  constructor(private elementRef: ElementRef) {}

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Updates the form control's disabled state based on the
   * initial configuration when the component is first initialized.
   */
  ngOnInit(): void {
    this.updateDisabledState();
  }

  /**
   * Handles changes to component inputs.
   *
   * @remarks Updates the form control's disabled state when the control
   * configuration changes, ensuring the UI stays in sync with the config.
   *
   * @param changes - Object containing the changed input properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['controlConfig'] && !changes['controlConfig'].firstChange) {
      this.updateDisabledState();
    }
  }

  /**
   * Toggles the dropdown open/closed state.
   *
   * @remarks Opens or closes the dropdown while managing focus and touch state.
   * When opening, focuses the button and finds the current selection index.
   * When closing, marks the control as touched for validation.
   *
   * @param event - Optional mouse event to prevent default behavior
   */
  toggleDropdown(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
    }

    if (this.controlConfig.disabled) {
      return;
    }

    // If dropdown is closing, mark as touched to trigger validation
    if (this.isDropdownOpen) {
      const control = this.formGroup?.get(this.controlConfig.name);
      if (control) {
        control.markAsTouched();
      }
    }

    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      setTimeout(() => {
        // Set focus to the button when opening
        this.selectButton.nativeElement.focus();

        // Find the selected option index if any
        const control = this.formGroup?.get(this.controlConfig.name);
        const value = control?.value;
        if (value) {
          this.currentOptionIndex = this.controlConfig.options.findIndex(opt => opt.value === value);
        } else {
          this.currentOptionIndex = -1;
        }
      });
    }
  }

  /**
   * Selects an option and updates the form control value.
   *
   * @remarks Sets the form control value, marks it as touched, closes the
   * dropdown, and announces the selection to screen readers.
   *
   * @param option - The option object to select
   * @param event - Optional mouse event to prevent default behavior
   */
  selectOption(option: SelectOption, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const control = this.formGroup?.get(this.controlConfig.name);
    if (control) {
      control.setValue(option.value);
      control.markAsTouched();
    }

    this.toggleDropdown();
    this.announceSelectedOption(option.label);
  }

  /**
   * Handles document click events for closing the dropdown.
   *
   * @remarks Implements click-outside behavior to close the dropdown when users
   * click outside the component. Also marks the control as touched for validation.
   *
   * @param event - The mouse click event
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Close dropdown if clicked outside
    if (this.isDropdownOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;

      // Mark control as touched when closing by clicking outside
      const control = this.formGroup?.get(this.controlConfig.name);
      if (control) {
        control.markAsTouched();
      }
    }
  }

  /**
   * Handles keyboard events for accessibility and navigation.
   *
   * @remarks Implements comprehensive keyboard support including:
   * - Opening dropdown with Enter, Space, Arrow keys
   * - Navigation with Arrow keys when open
   * - Selection with Enter/Space
   * - Closing with Escape
   * - Type-ahead functionality with alphanumeric keys
   *
   * @param event - The keyboard event
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isDropdownOpen) {
      // Open dropdown on Space, Enter, Down Arrow, Up Arrow
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.toggleDropdown();
      }
      return;
    }

    // Handle keyboard navigation when dropdown is open
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.isDropdownOpen = false;

        // Mark control as touched when closing with Escape key
        const control = this.formGroup?.get(this.controlConfig.name);
        if (control) {
          control.markAsTouched();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocusDown();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocusUp();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.currentOptionIndex >= 0) {
          this.selectOption(this.controlConfig.options[this.currentOptionIndex]);
        }
        break;
      default:
        // Handle alphanumeric key presses
        if (event.key.length === 1 && /[a-zA-Z0-9]/.test(event.key)) {
          event.preventDefault();
          this.handleAlphanumericKeyPress(event.key);
        }
        break;
    }
  }

  /**
   * Moves focus to the next option in the dropdown.
   *
   * @remarks Increments the current option index if not at the end of the list
   * and scrolls the focused option into view for better visibility.
   */
  moveFocusDown(): void {
    if (this.currentOptionIndex < this.controlConfig.options.length - 1) {
      this.currentOptionIndex++;
      this.scrollOptionIntoView();
    }
  }

  /**
   * Moves focus to the previous option in the dropdown.
   *
   * @remarks Decrements the current option index if not at the beginning of the list
   * and scrolls the focused option into view for better visibility.
   */
  moveFocusUp(): void {
    if (this.currentOptionIndex > 0) {
      this.currentOptionIndex--;
      this.scrollOptionIntoView();
    }
  }

  /**
   * Scrolls the currently focused option into view.
   *
   * @remarks Ensures the focused option is visible in the dropdown by scrolling
   * the listbox container if necessary. Uses 'nearest' scroll behavior for
   * smooth user experience.
   */
  scrollOptionIntoView(): void {
    if (this.currentOptionIndex >= 0 && this.listbox && this.listbox.nativeElement) {
      const options = this.listbox.nativeElement.querySelectorAll('[role="option"]');
      if (options && options.length > this.currentOptionIndex) {
        options[this.currentOptionIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }

  /**
   * Handles type-ahead functionality for quick option selection.
   *
   * @remarks Implements type-ahead by finding options that start with the typed
   * character. Cycles through matching options on repeated presses of the same
   * character. Updates focus and scrolls to the matching option.
   *
   * @param key - The alphanumeric character that was typed
   */
  handleAlphanumericKeyPress(key: string): void {
    const typedChar = key.toLowerCase();

    if (this.lastTypedChar !== typedChar) {
      this.lastMatchingIndex = 0;
    }

    const matchingOptions = this.controlConfig.options.filter(option => option.label.toLowerCase().startsWith(typedChar));

    if (matchingOptions.length) {
      if (this.lastMatchingIndex >= matchingOptions.length) {
        this.lastMatchingIndex = 0;
      }

      const optionToFocus = matchingOptions[this.lastMatchingIndex];
      this.currentOptionIndex = this.controlConfig.options.findIndex(opt => opt.value === optionToFocus.value);

      this.scrollOptionIntoView();
      this.lastMatchingIndex++;
    }

    this.lastTypedChar = typedChar;
  }

  /**
   * Announces the selected option to screen readers.
   *
   * @remarks Creates a temporary announcement element with aria-live="assertive"
   * to inform screen reader users of the selection change. The element is
   * automatically removed after the announcement.
   *
   * @param label - The label of the selected option to announce
   */
  announceSelectedOption(label: string): void {
    // Create a temporary element for screen reader announcement
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'assertive');
    announcer.classList.add('sr-only');
    announcer.textContent = `Ausgewählte Option: ${label}`;
    document.body.appendChild(announcer);

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  }

  /**
   * Updates the form control's enabled/disabled state.
   *
   * @remarks Enables or disables the form control based on the configuration
   * settings, ensuring proper form behavior and accessibility.
   */
  private updateDisabledState(): void {
    const control = this.formGroup?.get(this.controlConfig.name);
    if (control) {
      if (this.controlConfig.disabled) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }
}
